/**
 * @file CommandCenter.tsx
 * @description "Centrum dowodzenia" — the default landing page of this personal dashboard
 * instance. Renders the daily briefing ingested via POST /api/briefing/ingest (headline,
 * client cards, agent work log, pending decisions) together with a live systems metric from
 * GET /api/vps-status. The briefing is polled every 60 seconds and the VPS status every
 * 30 seconds; a yellow staleness banner appears when the briefing is older than 3 hours or
 * carries an explicit staleness note. All page copy is intentionally Polish (see the
 * commandCenter i18n namespace) since Polish is the deliberate language of this instance.
 * @author Son Nguyen <hoangson091104@gmail.com>
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock,
  Compass,
  Inbox,
  Loader,
  RefreshCw,
  Scale,
  Server,
  Users,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "../lib/api";
import type { Briefing, BriefingAgentWork, BriefingClient, VpsTargetStatus } from "../lib/api";
import { eventBus } from "../lib/eventBus";
import { EmptyState } from "../components/EmptyState";

const BRIEFING_POLL_MS = 60_000;
const VPS_POLL_MS = 30_000;
const STALE_AFTER_MS = 3 * 60 * 60 * 1000;

const BADGE_TONE_STYLES: Record<BriefingClient["badgeTone"], string> = {
  ok: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  warn: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  danger: "bg-red-500/10 text-red-400 border-red-500/20",
};

const AGENT_STATUS_ICONS: Record<BriefingAgentWork["status"], { icon: LucideIcon; cls: string }> = {
  done: { icon: CheckCircle2, cls: "text-emerald-400" },
  "in-progress": { icon: Loader, cls: "text-amber-400" },
  error: { icon: XCircle, cls: "text-red-400" },
};

// The briefing comes from an external generator; tolerate values outside the
// contract enums instead of crashing the default route on a lookup miss.
const DEFAULT_AGENT_ICON = AGENT_STATUS_ICONS["in-progress"];
const DEFAULT_BADGE_STYLE = BADGE_TONE_STYLES.warn;

/** Header date, e.g. "wtorek, 10 czerwca" — capitalized to "Wtorek, 10 czerwca". */
function formatHeaderDatePl(date: Date): string {
  const formatted = new Intl.DateTimeFormat("pl-PL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/** Relative age in Polish for the staleness banner ("3 godz. temu"). */
function relativeAgePl(iso: string): string {
  const ms = Math.max(0, Date.now() - new Date(iso).getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "przed chwilą";
  if (minutes < 60) return `${minutes} min temu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} godz. temu`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 dzień temu" : `${days} dni temu`;
}

function MetricTile({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone: "ok" | "warn" | "neutral";
}) {
  const valueCls =
    tone === "ok" ? "text-emerald-400" : tone === "warn" ? "text-amber-400" : "text-gray-100";
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5 text-accent" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 truncate">
          {label}
        </p>
        <p className={`text-base font-semibold truncate ${valueCls}`}>{value}</p>
      </div>
    </div>
  );
}

function ClientCard({ client }: { client: BriefingClient }) {
  const { t } = useTranslation("commandCenter");
  return (
    <div className="card-hover p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-gray-100 truncate">{client.name}</h3>
        <span
          className={`badge flex-shrink-0 ${BADGE_TONE_STYLES[client.badgeTone] ?? DEFAULT_BADGE_STYLE}`}
        >
          {client.badge}
        </span>
      </div>

      <p className="text-xs text-gray-400">{client.description}</p>

      {client.progressPct != null && (
        <div>
          <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
            <span>{t("progress")}</span>
            <span className="font-mono text-gray-300">{Math.round(client.progressPct)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
            <div
              className="h-full bg-accent/70 rounded-full transition-[width] duration-300"
              style={{ width: `${Math.min(100, Math.max(0, client.progressPct))}%` }}
            />
          </div>
        </div>
      )}

      <div className="space-y-1.5 text-xs">
        {client.lastContact && (
          <div className="flex items-start justify-between gap-3">
            <span className="text-gray-500 flex-shrink-0">{t("lastContact")}</span>
            <span className="text-gray-300 text-right">{client.lastContact}</span>
          </div>
        )}
        <div className="flex items-start justify-between gap-3">
          <span className="text-gray-500 flex-shrink-0">{t("nextStep")}</span>
          <span className="text-gray-200 text-right">{client.nextStep}</span>
        </div>
        {client.overdue && (
          <div className="flex items-start justify-between gap-3">
            <span className="text-red-400 flex-shrink-0 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" aria-hidden />
              {t("overdue")}
            </span>
            <span className="text-red-400 text-right">{client.overdue}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function CommandCenter() {
  const { t } = useTranslation("commandCenter");
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [briefingMissing, setBriefingMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [vpsTargets, setVpsTargets] = useState<VpsTargetStatus[] | null>(null);
  const wsConnected = useSyncExternalStore(eventBus.onConnection, () => eventBus.connected);

  const loadBriefing = useCallback(async () => {
    try {
      const result = await api.briefing.get();
      setBriefing(result);
      setBriefingMissing(result === null);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVps = useCallback(async () => {
    try {
      const result = await api.vpsStatus.get();
      setVpsTargets(result.targets);
    } catch {
      // The systems metric degrades to "—" when the endpoint is unreachable.
      setVpsTargets(null);
    }
  }, []);

  useEffect(() => {
    loadBriefing();
    const interval = setInterval(loadBriefing, BRIEFING_POLL_MS);
    return () => clearInterval(interval);
  }, [loadBriefing]);

  useEffect(() => {
    loadVps();
    const interval = setInterval(loadVps, VPS_POLL_MS);
    return () => clearInterval(interval);
  }, [loadVps]);

  const vpsUp = vpsTargets?.filter((x) => x.status === "up").length ?? 0;
  const vpsTotal = vpsTargets?.length ?? 0;
  const allSystemsUp = vpsTotal > 0 && vpsUp === vpsTotal;
  const decisionsCount = briefing?.decisions.length ?? 0;
  const agentWorkCount = briefing?.agentWork.length ?? 0;

  const generatedAge = briefing ? Date.now() - new Date(briefing.generatedAt).getTime() : 0;
  const isStale = Boolean(
    briefing && (briefing.staleness != null || generatedAge > STALE_AFTER_MS)
  );

  return (
    <div className="animate-fade-in space-y-8">
      {/* Staleness banner */}
      {briefing && isStale && (
        <div className="card p-3 flex items-center gap-3 border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-300 min-w-0">
            {t("staleBanner", { age: relativeAgePl(briefing.generatedAt) })}
            {briefing.staleness && (
              <span className="text-amber-400/80"> · {briefing.staleness}</span>
            )}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
              <Compass className="w-4.5 h-4.5 text-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-100">
                  {t("title")} — {formatHeaderDatePl(new Date())}
                </h1>
                {wsConnected ? (
                  <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                    {t("common:live")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400 bg-gray-500/10 border border-gray-500/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    {t("common:offline")}
                  </span>
                )}
              </div>
              {briefing && (
                <p className="text-xs text-gray-400 flex items-center gap-2">
                  {briefing.headline}
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 bg-surface-2 border border-border px-2 py-0.5 rounded-md font-mono ml-2">
                    <Clock className="w-3 h-3" />
                    {lastUpdate.toLocaleTimeString()}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
        <button onClick={loadBriefing} className="btn-ghost flex-shrink-0" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t("common:refresh")}
        </button>
      </div>

      {/* Metric bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricTile
          icon={Server}
          label={t("metricSystems")}
          value={vpsTargets ? t("metricSystemsValue", { up: vpsUp, total: vpsTotal }) : "—"}
          tone={allSystemsUp ? "ok" : "neutral"}
        />
        <MetricTile
          icon={Bot}
          label={t("metricAgents")}
          value={briefing ? String(agentWorkCount) : "—"}
          tone="neutral"
        />
        <MetricTile
          icon={Scale}
          label={t("metricDecisions")}
          value={briefing ? String(decisionsCount) : "—"}
          tone={decisionsCount > 0 ? "warn" : "neutral"}
        />
      </div>

      {/* Error state (endpoint unreachable) */}
      {error && !briefing && !briefingMissing && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">{t("loadError")}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !briefing && !briefingMissing && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse h-40 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty state — no briefing ingested yet */}
      {briefingMissing && !briefing && (
        <EmptyState icon={Inbox} title={t("emptyTitle")} description={t("emptyDescription")} />
      )}

      {briefing && (
        <>
          {/* Clients */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-accent" aria-hidden />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
                {t("clientsTitle")}
              </h2>
            </div>
            {briefing.clients.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {briefing.clients.map((client) => (
                  <ClientCard key={client.id} client={client} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">{t("noClients")}</p>
            )}
          </section>

          {/* Agent work + decisions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card p-4">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-border/60">
                <Bot className="w-4 h-4 text-accent" aria-hidden />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
                  {t("agentWorkTitle")}
                </h2>
              </div>
              {briefing.agentWork.length > 0 ? (
                <ul className="space-y-2">
                  {briefing.agentWork.map((item, i) => {
                    const { icon: Icon, cls } =
                      AGENT_STATUS_ICONS[item.status] ?? DEFAULT_AGENT_ICON;
                    return (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cls}`} aria-hidden />
                        <span className="text-gray-300 min-w-0">{item.text}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-xs text-gray-500 italic">{t("noAgentWork")}</p>
              )}
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-border/60">
                <Scale className="w-4 h-4 text-amber-400" aria-hidden />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">
                  {t("decisionsTitle")}
                </h2>
              </div>
              {briefing.decisions.length > 0 ? (
                <ul className="space-y-3">
                  {briefing.decisions.map((decision, i) => (
                    <li key={i} className="text-sm">
                      <p className="text-gray-100 font-medium">{decision.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{decision.context}</p>
                      {decision.since && (
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {t("since", { since: decision.since })}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500 italic">{t("noDecisions")}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
